import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Paper,
  IconButton,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import Button from "components/common/Button";
import { useEffect, useState } from "react";

const OPERATORS = ["=", "!=", ">", "<"];

const NotificationRuleModal = ({
  open,
  onClose,
  onSave,
  mode = "create",
  rule,
}) => {
  const [triggerCriteria, setTriggerCriteria] = useState([
    { path: "", operator: "=", value: "" },
  ]);
  const [recipients, setRecipients] = useState([""]);
  const [subject, setSubject] = useState("");
  const [template, setTemplate] = useState("");
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (mode === "edit" && rule) {
      setTriggerCriteria(
        (rule.triggerCriteria || [{ path: "", operator: "=", value: "" }]).map(
          (c) => ({
            ...c,
            value: String(c.value ?? ""),
          })
        )
      );
      setRecipients(rule.recipients || [""]);
      setSubject(rule.subject || "");
      setTemplate(rule.template || "");
    } else if (mode === "create") {
      setTriggerCriteria([{ path: "", operator: "=", value: "" }]);
      setRecipients([""]);
      setSubject("");
      setTemplate("");
    }
    setFormErrors({});
  }, [mode, rule, open]);

  const parseValue = (value) => {
    const trimmedValue = value.trim();

    if (trimmedValue.toLowerCase() === "true") {
      return true;
    }
    if (trimmedValue.toLowerCase() === "false") {
      return false;
    }

    if (trimmedValue !== "" && !isNaN(trimmedValue)) {
      return Number(trimmedValue);
    }

    return trimmedValue;
  };

  const handleClose = () => {
    onClose();
  };

  const validateForm = () => {
    const errors = {};

    // Validate trigger criteria
    const hasValidCriteria = triggerCriteria.some(
      (c) => c.path.trim() && c.value.trim()
    );
    if (!hasValidCriteria) {
      errors.criteria = "At least one valid trigger criterion is required";
    }

    // Validate recipients
    const hasValidRecipient = recipients.some((r) => r.trim());
    if (!hasValidRecipient) {
      errors.recipients = "At least one recipient email is required";
    }

    // Validate subject
    if (!subject.trim()) {
      errors.subject = "Subject is required";
    }

    // Validate template
    if (!template.trim()) {
      errors.template = "Template is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const ruleData = {
      triggerCriteria: triggerCriteria
        .filter((c) => c.path.trim() && c.value.trim())
        .map((c) => ({
          ...c,
          value: parseValue(c.value),
        })),
      recipients: recipients.filter((r) => r.trim()),
      subject: subject.trim(),
      template: template.trim(),
    };

    onSave(ruleData);
    handleClose();
  };

  const addCriteria = () => {
    setTriggerCriteria([
      ...triggerCriteria,
      { path: "", operator: "=", value: "" },
    ]);
  };

  const removeCriteria = (index) => {
    setTriggerCriteria(triggerCriteria.filter((_, i) => i !== index));
  };

  const updateCriteria = (index, field, value) => {
    const updated = [...triggerCriteria];
    updated[index] = { ...updated[index], [field]: value };
    setTriggerCriteria(updated);
  };

  const addRecipient = () => {
    setRecipients([...recipients, ""]);
  };

  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const updateRecipient = (index, value) => {
    const updated = [...recipients];
    updated[index] = value;
    setRecipients(updated);
  };

  const hasValidData =
    triggerCriteria.some((c) => c.path.trim() && c.value.trim()) &&
    recipients.some((r) => r.trim()) &&
    subject.trim() &&
    template.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"md"} fullWidth>
      <DialogTitle>
        {mode === "edit"
          ? "Edit Notification Rule"
          : "Create Notification Rule"}
        <IconButton
          aria-label={"close"}
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size={"small"}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Paper elevation={0} sx={{ padding: "1rem" }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Trigger Criteria Section */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pb: 2,
                }}
              >
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  {"Trigger Criteria"}
                </Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography
                        variant={"body2"}
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        {"Examples:"}
                      </Typography>
                      <Typography
                        variant={"body2"}
                        sx={{ fontFamily: "monospace" }}
                      >
                        {'".program.auditable" = "true"'}
                      </Typography>
                      <Typography
                        variant={"body2"}
                        sx={{ fontFamily: "monospace" }}
                      >
                        {'".layers[].status" = "BOUND"'}
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <IconButton size={"small"} sx={{ p: 0.5 }}>
                    <InfoIcon fontSize={"small"} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Stack spacing={2}>
                {triggerCriteria.map((criteria, index) => (
                  <Stack
                    key={index}
                    direction={"row"}
                    spacing={1}
                    alignItems={"center"}
                  >
                    <TextField
                      label={"Path"}
                      value={criteria.path}
                      onChange={(e) =>
                        updateCriteria(index, "path", e.target.value)
                      }
                      size={"small"}
                      sx={{ flex: 2 }}
                    />
                    <FormControl size={"small"} sx={{ minWidth: 80 }}>
                      <InputLabel>{"Operator"}</InputLabel>
                      <Select
                        value={criteria.operator}
                        onChange={(e) =>
                          updateCriteria(index, "operator", e.target.value)
                        }
                        label={"Operator"}
                      >
                        {OPERATORS.map((op) => (
                          <MenuItem key={op} value={op}>
                            {op}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label={"Value"}
                      value={criteria.value}
                      onChange={(e) =>
                        updateCriteria(index, "value", e.target.value)
                      }
                      size={"small"}
                      sx={{ flex: 2 }}
                      placeholder={"e.g., bound"}
                    />
                    {index === triggerCriteria.length - 1 ? (
                      <IconButton
                        onClick={addCriteria}
                        color={"primary"}
                        size={"small"}
                        sx={{
                          border: "2px solid",
                          borderColor: "primary.main",
                          borderRadius: "4px",
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => removeCriteria(index)}
                        color={"default"}
                        size={"small"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
              {formErrors.criteria && (
                <Typography variant={"caption"} color={"error"} sx={{ mt: 1 }}>
                  {formErrors.criteria}
                </Typography>
              )}
            </Box>

            {/* Recipients Section */}
            <Box>
              <Typography variant={"subtitle2"} sx={{ pb: 2, fontWeight: 600 }}>
                {"Recipients"}
              </Typography>
              <Stack spacing={2}>
                {recipients.map((email, index) => (
                  <Stack
                    key={index}
                    direction={"row"}
                    spacing={1}
                    alignItems={"center"}
                  >
                    <TextField
                      label={"Email"}
                      value={email}
                      onChange={(e) => updateRecipient(index, e.target.value)}
                      size={"small"}
                      fullWidth
                      type={"email"}
                      placeholder={"user@example.com"}
                    />
                    {index === recipients.length - 1 ? (
                      <IconButton
                        onClick={addRecipient}
                        color={"primary"}
                        size={"small"}
                        sx={{
                          border: "2px solid",
                          borderColor: "primary.main",
                          borderRadius: "4px",
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => removeRecipient(index)}
                        color={"default"}
                        size={"small"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
              {formErrors.recipients && (
                <Typography variant={"caption"} color={"error"} sx={{ mt: 1 }}>
                  {formErrors.recipients}
                </Typography>
              )}
            </Box>

            {/* Message Format Section */}
            <Box>
              <Typography variant={"subtitle2"} sx={{ pb: 2, fontWeight: 600 }}>
                {"Message Format"}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label={"Subject"}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                  size={"small"}
                  error={Boolean(formErrors.subject)}
                  helperText={
                    formErrors.subject ||
                    "Supports template variables like {{insured}}, {{reference}}"
                  }
                  placeholder={"Notification subject"}
                />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant={"body2"} color={"text.secondary"}>
                      {"Template"}
                    </Typography>
                  </Box>
                  <TextField
                    value={template}
                    onChange={(e) => setTemplate(e.target.value)}
                    fullWidth
                    multiline
                    rows={6}
                    size={"small"}
                    error={Boolean(formErrors.template)}
                    helperText={formErrors.template || ""}
                    placeholder={
                      "Use template variables like {{state.input.program.inception}}"
                    }
                  />
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          color={"primary"}
          sx={{ width: 100 }}
          disabled={!hasValidData}
        >
          {"Save"}
        </Button>
        <Button onClick={handleClose} color={"secondary"} sx={{ width: 100 }}>
          {"Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationRuleModal;
